import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Badge, Row, Col, Form, Table, Modal, OverlayTrigger, Popover, Spinner, Alert, Tooltip } from 'react-bootstrap';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { QuestionCircle, PhoneVibrate, PhoneVibrateFill, Check, Clipboard } from 'react-bootstrap-icons';
import './styles/colors.css';
import { patients } from '../data/patientData';
import { updateMessageStatus, getPatientMessages, updateMessages, saveTestCases } from '../api';
import TextTimeline from './TextTimeline';
import MedicationTable from './MedicationTable';
import { mergeEquivalentMedicationsForDisplay } from '../utils/medicationEquivalence';
import {
  NO_MEDICATIONS_DETECTED_MESSAGE,
  buildMedManagementApprovalNote,
  collectThromboAndBleedParts,
  filterApplicableMedications,
  formatMedicationAction,
  medicationDosagePresent,
} from '../utils/medManagementApprovalNote';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

/** Renders items as a, b, and c with each term wrapped in <strong> (display text lowercased). */
function BoldOxfordList({ items }) {
  const a = [
    ...new Set(
      items
        .map((x) => String(x).trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  if (a.length === 0) return null;
  if (a.length === 1) return <strong>{a[0]}</strong>;
  if (a.length === 2) {
    return (
      <>
        <strong>{a[0]}</strong> and <strong>{a[1]}</strong>
      </>
    );
  }
  return (
    <>
      {a.slice(0, -1).map((x, i) => (
        <React.Fragment key={`${x}-${i}`}>
          {i > 0 ? ', ' : null}
          <strong>{x}</strong>
        </React.Fragment>
      ))}
      {', and '}
      <strong>{a[a.length - 1]}</strong>
    </>
  );
}

function PatientSummaryCard({
  patient = patients[0],
  onApprove,
  recommendation = {},
  isLoading,
  isUpdatingRecommendation,
  onMessagesRefresh,
  refreshTrigger,
  appointmentId: appointmentIdProp,
  archiveView = false
}) {
  const [status, setStatus] = useState('pending');
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', title: '', message: '' });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRationale, setShowRationale] = useState(false);
  const [messages, setMessages] = useState([]);
  const [editedMessages, setEditedMessages] = useState([]);
  const [lockedTimelineItem, setLockedTimelineItem] = useState(null);
  const [showMedicationTable, setShowMedicationTable] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [approvalNoteCopied, setApprovalNoteCopied] = useState(false);
  const [pendingApprovalStatus, setPendingApprovalStatus] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);

  // Fetch messages when patient changes or refreshTrigger changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (patient?.id) {
        try {
          const fetchedMessages = await getPatientMessages(patient.id);
          setMessages(fetchedMessages);
          const initializedMessages = fetchedMessages.map(msg => {
            try {
              if (!msg.scheduled_date) {
                return { ...msg, isEdited: false };
              }

              // Try to parse the date string directly
              const dateStr = msg.scheduled_date;
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) {
                throw new Error('Invalid date');
              }
              
              // Then format it
              const formattedDate = format(date, 'yyyy-MM-dd');
              
              return {
                ...msg,
                scheduled_date: formattedDate,
                isEdited: false
              };
            } catch (dateError) {
              console.error('Date parsing error for message:', msg);
              return { ...msg, isEdited: false };
            }
          });
          setEditedMessages(initializedMessages);
        } catch (error) {
          console.error('Error fetching messages:', error);
        }
      }
    };
    
    fetchMessages();
  }, [patient, refreshTrigger]);

  useEffect(() => {
    if (!lockedTimelineItem) setIsExpanded(false);
  }, [lockedTimelineItem]);

  const mergedMedicationsForDisplay = useMemo(
    () =>
      recommendation?.parsed_output?.medications
        ? mergeEquivalentMedicationsForDisplay(recommendation.parsed_output.medications)
        : {},
    [recommendation?.parsed_output?.medications]
  );

  const applicableMedicationsForDisplay = useMemo(
    () => filterApplicableMedications(mergedMedicationsForDisplay),
    [mergedMedicationsForDisplay]
  );

  // Guard against undefined patient
  if (!patient) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <p>Please select a patient</p>
        </Card.Body>
      </Card>
    );
  }

  const patientNumber = patient.name.match(/\d+$/)?.[0] || '1';
  const displayNumber = parseInt(patientNumber).toString();

  const handleSaveChanges = () => {
    const editCount = editedMessages.filter(msg => msg.isEdited).length;
    setConfirmAction({
      type: 'modify',
      title: 'Confirm Changes',
      message: `Are you sure you want to save changes to ${editCount} message${editCount !== 1 ? 's' : ''}?`
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (confirmAction.type === 'modify') {
      try {
        const updates = editedMessages
          .filter(msg => msg.isEdited)
          .map(msg => ({
            message_id: msg.message_id,
            message_text: msg.message_text,
            scheduled_date: msg.scheduled_date
          }));

       
        const result = await updateMessages(updates);
       

        if (result.success) {
          const updatedMessages = await getPatientMessages(patient.id);
          setMessages(updatedMessages);
          setEditedMessages(updatedMessages.map(msg => ({
            ...msg,
            isEdited: false
          })));
          setIsEditing(false);
        } else {
          throw new Error('Update was not successful');
        }
      } catch (error) {
        console.error('Error in handleConfirm:', error);
        alert('Failed to update messages');
      }
    }
    setShowConfirmModal(false);
  };




  // Convert messages to calendar events
  const calendarEventsMessages = messages.map(message => ({
    title: message.message_text,
    start: new Date(message.scheduled_date),
    end: new Date(message.scheduled_date),
    allDay: true,
    type: 'sms'
  }));

  // Custom event styling
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.type === 'sms' ? '#28a745' : '#007bff',
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const recommendationRationale = (
    <Popover id="recommendation-rationale" style={{ maxWidth: '400px' }}>
      <Popover.Header as="h3">About Our Recommendations</Popover.Header>
      <Popover.Body>
        <p className="mb-0">
          Our patent-pending algorithm analyzes patient records and cross-references them with the latest medical research to provide personalized, evidence-based treatment recommendations.
        </p>
      </Popover.Body>
    </Popover>
  );

  <>
    <div>
      <small className="d-block mt-2 text-muted">
        {recommendation?.reason_coding_code || 'TBD'}
      </small>
      <small className="d-block fw-bold">
        {recommendation?.reason_coding_code || 'Surgery'}
      </small>
    </div>

    <div>
      <small className="d-block mt-2 text-muted">TBD</small>
      <small className="d-block fw-bold" style={{ whiteSpace: 'nowrap' }}>No medication changes needed</small>
    </div>
  </>;

  const handleStatusUpdate = (status) => {
    console.log('=== APPROVE/DENY BUTTON CLICKED ===');
    console.log('Status:', status);
    console.log('Patient ID:', patient?.id);
    console.log('Patient object:', patient);
    
    if (!patient?.id) {
        console.error('No valid patient found');
        alert('No valid patient found');
        return;
    }

    // Show the approval modal instead of window.prompt
    setPendingApprovalStatus(status);
    setApprovalNote(
      buildMedManagementApprovalNote({
        parsedOutput: recommendation?.parsed_output,
        mergedMedications: mergedMedicationsForDisplay,
        status,
      })
    );
    setApprovalNoteCopied(false);
    setShowApprovalModal(true);
  };

  const handleCopyApprovalNote = async () => {
    const text = approvalNote || '';
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'absolute';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setApprovalNoteCopied(true);
      window.setTimeout(() => setApprovalNoteCopied(false), 1800);
    } catch {
      setApprovalNoteCopied(false);
    }
  };

  const approvalAppointmentId =
    appointmentIdProp ??
    (recommendation?.appointment_id != null && recommendation?.appointment_id !== ''
      ? String(recommendation.appointment_id)
      : null);

  const handleApprovalConfirm = async () => {
    if (!pendingApprovalStatus || !patient?.id) {
      return;
    }
    if (!approvalAppointmentId) {
      alert('No appointment is linked to this recommendation. Cannot approve or deny.');
      return;
    }

    setIsSubmittingApproval(true);

    try {
      console.log('Calling updateMessageStatus API...');
      console.log('API call parameters:', {
        patientId: patient.id,
        status: pendingApprovalStatus,
        note: approvalNote || '',
        appointmentId: approvalAppointmentId
      });
      
      const result = await updateMessageStatus(
        patient.id,
        pendingApprovalStatus,
        approvalNote || '',
        approvalAppointmentId
      );
      console.log('API call result:', result);
      
      setShowApprovalModal(false);
      setPendingApprovalStatus(null);
      setApprovalNote('');
      setIsSubmittingApproval(false);
      onApprove?.({
        status: pendingApprovalStatus,
        note: approvalNote || '',
        appointmentId: approvalAppointmentId,
      });
    } catch (error) {
      console.error('Error updating message status:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`Failed to update message status: ${error.message}`);
      setIsSubmittingApproval(false);
      setShowApprovalModal(false);
    }
  };

  const handleTextChange = (messageId, newText) => {
    setEditedMessages(prev => prev.map(msg => 
      msg.message_id === messageId 
        ? { ...msg, message_text: newText, isEdited: true }
        : msg
    ));
  };

  const handleDateChange = (messageId, newDate) => {
    setEditedMessages(prev => prev.map(msg => 
      msg.message_id === messageId 
        ? { ...msg, scheduled_date: newDate, isEdited: true }
        : msg
    ));
  };

  const renderMessageTable = () => (
    <div>
      {editedMessages.length === 0 ? (
        <div className="text-center p-4 text-muted">
          <p>In order to generate texts, we need a surgery date and medication on record for this patient.</p>
        </div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th className="col-2">Scheduled Date</th>
              <th className="col-9">Message</th>
              <th className="col-1">Sent</th>
            </tr>
          </thead>
          <tbody>
            {editedMessages.map((message) => (
              <tr key={message.message_id}>
                <td>
                  <Form.Control
                    type="text"
                    value={message.scheduled_date.split('T')[0]}
                    onChange={(e) => handleDateChange(message.message_id, e.target.value)}
                    disabled={message.sent}
                    placeholder="YYYY-MM-DD"
                  />
                </td>
                <td>
                  <Form.Control
                    as="textarea"
                    value={message.message_text}
                    onChange={(e) => handleTextChange(message.message_id, e.target.value)}
                    disabled={message.sent}
                  />
                </td>
                <td>{message.sent ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );

  // Find surgery info for timeline
  const surgeryInfo = recommendation?.inputs?.find(input => input.scheduled_surgery)?.scheduled_surgery;
  const surgeryDate = surgeryInfo?.date;
  const procedureName = recommendation?.reason_coding_code || 'Surgery';

  const formatProcedureNames = (procedureList) => {
    if (!procedureList) return 'Unknown procedure';
    
    // Handle both string and array cases
    const procedures = Array.isArray(procedureList) ? procedureList : [procedureList];
    
    return procedures
      .map(proc => 
        proc.replace(/_/g, ' ') // Replace underscores with spaces
            .trim()
            .split(' ')
            .map(word => {
              // Keep ACDF and TLIF capitalized
              if (word.toUpperCase() === 'ACDF' || word.toUpperCase() === 'TLIF') {
                return word.toUpperCase();
              }
              // Capitalize other words normally
              return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(' ')
      )
      .join(', ');
  };

  const handleMedicationChange = (updatedMedications) => {
    // Here you would typically update the recommendation data
    // This is a placeholder for the actual implementation
    console.log('Medications updated:', updatedMedications);
  };

  const handleSaveTestCases = async (testCases) => {
    try {
      const testCaseData = {
        patient_id: patient?.id,
        recommendation_input: recommendation?.inputs,
        test_cases: testCases.map(testCase => ({
          medication: testCase.medication,
          details: testCase.details,
          timestamp: testCase.timestamp
        }))
      };

      const result = await saveTestCases(testCaseData);
      
      if (result.success) {
        // You might want to show a success message here
        console.log('Test cases saved successfully');
      } else {
        throw new Error('Failed to save test cases');
      }
    } catch (error) {
      console.error('Error saving test cases:', error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <>
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          {archiveView && (
            <Alert variant="secondary" className="py-2 small mb-4">
              Viewing a <strong>past / inactive</strong> appointment (read-only). Approve, deny, and notes
              apply to <strong>active</strong> appointments from the picker above.
            </Alert>
          )}
          {/* Recommendation and Action Buttons Row */}
          <Card className="mb-4 bg-light">
            <Card.Body>
              <Row>
                <Col md={9}>
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <i className="fas fa-lightbulb text-warning" style={{ fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="mb-0 me-2">Springhouse Safer Surgery Recommendation</h6>
                        <OverlayTrigger
                          trigger={['hover', 'focus']}
                          placement="right"
                          overlay={recommendationRationale}
                        >
                          <div className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
                            <QuestionCircle className="text-muted" size={16} />
                          </div>
                        </OverlayTrigger>
                      </div>
                      {isUpdatingRecommendation ? (
                        <div className="d-flex align-items-center mt-3">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <span>Updating recommendation...</span>
                        </div>
                      ) : (
                        <>
                          <small className="text-muted d-block mb-2">
                            Generated on {recommendation?.created_at ? 
                              new Date(recommendation.created_at).toLocaleString() : 
                              'Unknown time'}
                          </small>
                          <p className="mb-2" style={{ fontSize: '0.9rem' }}>
                            {recommendation?.parsed_output?.medications ? (
                              applicableMedicationsForDisplay.length > 0 ? (
                                <div>
                                  {applicableMedicationsForDisplay.map(([med, details]) => (
                                      <div key={med} className="mb-2">
                                        <strong>{med}:</strong>{' '}
                                        {medicationDosagePresent(details) ? (
                                          <span className="text-muted">
                                            Dosage: {details.dosage}
                                          </span>
                                        ) : (
                                          <>
                                            <span className="text-muted">Dosage: </span>
                                            <OverlayTrigger
                                              trigger={['hover', 'focus']}
                                              placement="top"
                                              overlay={
                                                <Tooltip id={`ehr-dosage-missing-${med}`}>
                                                  There is no dosage listed for this medication in your EHR.
                                                  Please update it.
                                                </Tooltip>
                                              }
                                            >
                                              <span
                                                className="d-inline-flex align-items-center"
                                                style={{ cursor: 'help' }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label="Dosage missing from electronic health record"
                                              >
                                                <Badge bg="danger" className="fw-normal px-2 py-1">
                                                  Dosage Missing from EHR
                                                </Badge>
                                              </span>
                                            </OverlayTrigger>
                                          </>
                                        )}
                                        <br />
                                        <span>{formatMedicationAction(med, details)}</span>
                                      </div>
                                    ))}
                                </div>
                              ) : (
                                NO_MEDICATIONS_DETECTED_MESSAGE
                              )
                            ) : (
                              <div className="d-flex align-items-center mt-3">
                                <Spinner animation="border" size="sm" className="me-2" />
                                <span>Generating recommendation...</span>
                            </div>
                            )}
                          </p>
                          
                          <div className="d-flex align-items-center">
                            <Button 
                              variant="link" 
                              className="p-0 text-muted"
                              onClick={() => setShowRationale(!showRationale)}
                              style={{ textDecoration: 'none' }}
                            >
                              {showRationale ? 'Hide Rationale' : 'View Rationale'}
                            </Button>
                          </div>
                          
                          {/* Expanded Rationale Section */}
                          {showRationale && !isUpdatingRecommendation && (() => {
                            const parsed = recommendation?.parsed_output;
                            const { thromboParts, bleedParts } = collectThromboAndBleedParts({
                              risks: parsed?.risks,
                              conditions: parsed?.conditions,
                              medicationKeys: Object.keys(mergedMedicationsForDisplay),
                            });
                            const hasThrombo = thromboParts.length > 0;
                            const hasBleed = bleedParts.length > 0;

                            return (
                              <div className="mt-3 border-top pt-3">
                                <p className="mb-0 small" style={{ lineHeight: 1.65 }}>
                                  This personalized recommendation, specific to the patient&apos;s current
                                  antithrombotic medication, is based on their modifiable patient factors.
                                  {hasThrombo || hasBleed ? (
                                    <>
                                      {' '}
                                      {hasThrombo && (
                                        <>
                                          Their risk of thrombosis is elevated due to{' '}
                                          <BoldOxfordList items={thromboParts} />
                                        </>
                                      )}
                                      {hasThrombo && hasBleed && ', while '}
                                      {hasBleed && (
                                        <>
                                          {hasThrombo ? 'their' : 'Their'} bleeding risk is elevated due to{' '}
                                          <BoldOxfordList items={bleedParts} />
                                        </>
                                      )}
                                      .
                                    </>
                                  ) : null}{' '}
                                  This pause/resumption schedule considers these risk factors in the context
                                  of the antithrombotic medication and the planned surgical procedure.
                                </p>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
                <Col md={3} className="align-self-start">
                  {!isEditing ? (
                    <>
                      <Button 
                        variant="primary"
                        className="mb-2 w-100"
                        onClick={() => handleStatusUpdate('approved')}
                        disabled={archiveView || isUpdatingRecommendation || !patient?.id}
                        style={{ backgroundColor: 'var(--action-approve)' }}
                      >
                        {isUpdatingRecommendation ? 'Updating...' : 'Approve'}
                      </Button>
                      <Button 
                        variant="primary"
                        className="mb-2 w-100"
                        onClick={() => setIsEditing(true)}
                        disabled={archiveView}
                        style={{ 
                          backgroundColor: 'var(--action-modify)',
                          borderColor: 'var(--action-modify)',
                          color: 'black'
                        }}
                      >
                        Modify
                      </Button>
                      <Button 
                        variant="primary"
                        className="w-100"
                        onClick={() => handleStatusUpdate('denied')}
                        disabled={archiveView || isUpdatingRecommendation || !patient?.id}
                        style={{ 
                          backgroundColor: 'var(--action-deny)',
                          borderColor: 'var(--action-deny)',
                          color: 'white'
                        }}
                      >
                        {isUpdatingRecommendation ? 'Updating...' : 'Deny'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        onClick={() => setIsEditing(false)}
                        className="me-2"
                        disabled={isUpdatingRecommendation}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSaveChanges}
                        disabled={isUpdatingRecommendation || !editedMessages.some(msg => msg.isEdited)}
                      >
                        {isUpdatingRecommendation ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
              {Object.keys(mergedMedicationsForDisplay).length > 0 && (
                            <div className="mt-3">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowMedicationTable(!showMedicationTable)}
                                className="mb-2"
                              >
                                {showMedicationTable ? 'Hide Test Cases' : 'Add Test Cases'}
                              </Button>
                              {showMedicationTable && (
                                <MedicationTable 
                                  medications={mergedMedicationsForDisplay}
                                  onMedicationChange={handleMedicationChange}
                                  onSaveTestCases={handleSaveTestCases}
                                />
                              )}
                            </div>
                          )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
          <Card.Header>
          <h6 className="mb-0">Timeline and Suggested Text Messages</h6>
            </Card.Header>
          <Card.Body>
            {/* Timeline and Calendar Section */}
          {!isEditing && (
            isUpdatingRecommendation ? (
              <div className="text-center my-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <div className="mb-10">
                <div className="d-flex justify-content-center">
                  <TextTimeline
                    messages={editedMessages}
                    procedureName={procedureName}
                    surgeryDate={surgeryDate}
                    onClick={(item) => {
                      setLockedTimelineItem((prev) =>
                        prev && item && String(prev.id) === String(item.id) ? null : item
                      );
                    }}
                    lockedId={lockedTimelineItem?.id ?? null}
                  />
                </div>
                {!lockedTimelineItem && (
                  <p className="text-muted small text-center px-2 mt-1 mb-0">
                    Click an icon to view and edit messaging for that day.
                  </p>
                )}
                {lockedTimelineItem ? (
                <Card className="mt-1">
                  <Card.Body className="p-2">
                      <div>
                        {lockedTimelineItem.type === 'surgery' ? (
                          <>
                            <div className="rounded border bg-light p-3">
                              <div className="small text-muted mb-1">Surgery milestone</div>
                              <div className="small">{lockedTimelineItem.text ?? ''}</div>
                            </div>
                            <p className="text-muted small mt-2 mb-0">
                              Edit suggested texts using the messaging (chat) icons on the timeline.
                            </p>
                          </>
                        ) : lockedTimelineItem.messages &&
                          lockedTimelineItem.messages.length > 0 ? (
                          lockedTimelineItem.messages.map((msg, index) => (
                            <div key={msg.message_id} className={index > 0 ? 'mt-2' : ''}>
                              <Form.Control
                                as="textarea"
                                value={msg.message_text ?? ''}
                                onChange={(e) => {
                                  const newText = e.target.value;
                                  setEditedMessages((prev) =>
                                    prev.map((m) =>
                                      m.message_id === msg.message_id
                                        ? { ...m, message_text: newText, isEdited: true }
                                        : m
                                    )
                                  );
                                }}
                                style={{ minHeight: 120 }}
                              />
                            </div>
                          ))
                        ) : (
                          <Form.Control
                            as="textarea"
                            value={lockedTimelineItem.text ?? ''}
                            onChange={(e) => {
                              const newText = e.target.value;
                              setEditedMessages((prev) =>
                                prev.map((msg) =>
                                  msg.message_id === lockedTimelineItem.id
                                    ? { ...msg, message_text: newText, isEdited: true }
                                    : msg
                                )
                              );
                            }}
                            style={{ minHeight: 120 }}
                          />
                        )}
                        {lockedTimelineItem.type !== 'surgery' && (
                        <div className="d-flex justify-content-end mt-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSaveChanges}
                          >
                            Save Changes
                          </Button>
                        </div>
                        )}
                      </div>
                  </Card.Body>
                </Card>
                ) : null}
                {lockedTimelineItem && (
                <>
                <div className="text-center mt-3">
                  <Button
                    variant="link"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? 'Show Less' : 'Show All Steps'}
                  </Button>
                </div>
                {isExpanded && (
                  <div style={{ height: '400px', marginBottom: '20px' }}>
                    <Calendar
                      localizer={localizer}
                      events={calendarEventsMessages}
                      startAccessor="start"
                      endAccessor="end"
                      eventPropGetter={eventStyleGetter}
                      views={['month']}
                      defaultView="month"
                    />
                  </div>
                )}
                </>
                )}
              </div>
            )
          )}

          {/* Message Table Section */}
          {isEditing && (
            <Card className="mt-4">
              <Card.Header>
                <h6 className="mb-0">Edit Text Messages</h6>
              </Card.Header>
              <Card.Body>
                {isUpdatingRecommendation ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading messages...</span>
                  </div>
                ) : (
                  renderMessageTable()
                )}
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{confirmAction.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmAction.message}</Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="primary"
            onClick={handleConfirm}
            disabled={isUpdatingRecommendation}
          >
            {isUpdatingRecommendation ? 'Saving...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approval/Denial Modal */}
      <Modal show={showApprovalModal} onHide={() => !isSubmittingApproval && setShowApprovalModal(false)}>
        <Modal.Header closeButton={!isSubmittingApproval}>
          <Modal.Title>
            Confirm {pendingApprovalStatus === 'approved' ? 'Approval' : 'Denial'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSubmittingApproval ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" className="mb-3" />
              <p className="text-muted">Processing your request...</p>
            </div>
          ) : (
            <>
              <p>
                Are you sure you want to {pendingApprovalStatus === 'approved' ? 'approve' : 'deny'} this recommendation?
              </p>
              <Form.Group className="mt-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Form.Label className="mb-0">Note</Form.Label>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCopyApprovalNote}
                    className="d-inline-flex align-items-center"
                    style={{ gap: '0.35rem' }}
                  >
                    {approvalNoteCopied ? <Check size={16} /> : <Clipboard size={16} />}
                    {approvalNoteCopied ? 'Copied' : 'Copy note'}
                  </Button>
                </div>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder="Medication plan and rationale..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowApprovalModal(false)}
            disabled={isSubmittingApproval}
          >
            Cancel
          </Button>
          <Button 
            variant={pendingApprovalStatus === 'approved' ? 'success' : 'danger'}
            onClick={handleApprovalConfirm}
            disabled={isSubmittingApproval}
            style={{
              backgroundColor: pendingApprovalStatus === 'approved' 
                ? 'var(--action-approve)' 
                : 'var(--action-deny)',
              borderColor: pendingApprovalStatus === 'approved' 
                ? 'var(--action-approve)' 
                : 'var(--action-deny)'
            }}
          >
            {isSubmittingApproval ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>Confirm {pendingApprovalStatus === 'approved' ? 'Approval' : 'Denial'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
          </Card.Body>
          </Card>

          
    </>
  );
}

export default PatientSummaryCard; 
